import { useEffect, useMemo, useState } from 'react';

import { makeStyles } from "@material-ui/core/styles";
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Add from "@material-ui/icons/Add";

import { useHistory, useParams, Link } from "react-router-dom";

import bookmarkedIcon from '../../assets/icons/bookmarked.png';
import unbookmarkedIcon from '../../assets/icons/unbookmarked.png';
import gitIcon from '../../assets/icons/git.svg';
import listIcon from '../../assets/icons/list.svg';
import addIcon from '../../assets/icons/add.svg';
import copyIcon from '../../assets/icons/copy.png';


import { useDispatchReducerContext, useStateReducerContext } from '../../store';
import { ADD_BOOKMARK, REMOVE_BOOKMARK, SHOW_TOAST } from '../../store/dispatchNames';
import CommonTooltip from '../../components/ui/CommonTooltip';
import { CommonDialog, CommonDialogActions, CommonDialogContent, CommonDialogTitle } from '../../components/ui/CommonDialog';
import CommonTextField from '../../components/ui/CommonTextField';
import CancelButton from '../../components/ui/CancelButton';
import CommonButton from '../../components/ui/CommonButton';
import { useCreateFeatureBranch, useFetchFeatureBranchs } from './useFeatureBranchs';
import { useHandleAwsAuthErr } from '../../common/AwsUtil';
import { List, ListItem, ListItemText } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    projectinfotitle: {
        color: '#4C4948',
        fontWeight: 400,
        lineHeight: '20px',
        marginTop: '12px',
        background: '#ECECEC',
        display: 'flex',
        flexDirection: 'row',
        borderBottom: '0.5px solid #CCCCCC'
    },
    projectinfocontent: {
        color: '#4D4E53',
        fontWeight: 400,
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'row',
        borderBottom: '0.5px solid #C4C4C4'
    },
    'git-icon': {
        position: 'relative',
        padding: '6px',
        '&:hover': {
            backgroundColor: '#F7EED7'
        }
    },
    'gitIcon': {
        width: '24px',
        height: '24px',
    },
    'smallGitIcon': {
        position: 'absolute',
        bottom: '6px',
        right: '6px',
        width: '10px',
        height: '10px',
    },
    gitLinkBtnArea: {
        position: 'absolute',
        right: '4px',
        top: '7px',
    },
    'icon-btn': {
        padding: '6px',
        '&:hover': {
            backgroundColor: '#F7EED7'
        },
        '&.bookmark': {
            position: 'absolute',
            right: '4px !important',
        }
    },
    bookmarkIcon: {
        width: '24px',
        height: '24px',
    },
    'projectinfo-name-content': {
        position: 'relative',
        paddingLeft: '28px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        width: '33%',
        fontSize: '13px',
        '&.feature': {
            fontSize: '13px',
            width: '20%',
        },
        '& .pipelinename': {
            fontSize: '13px',
            display: 'block',
            marginRight: '40px'
        }
    },
    'feature-area-content' : {
        position: 'relative',
        paddingLeft: '28px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        width: '13%',
        fontSize: '13px',
    },
    'projectinfo-area-content': {
        paddingLeft: '28px',
        width: '12.8%',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        textTransform: 'uppercase',
        '& p': {
            fontSize: '13px',
        }
    },
    'aws-account-content': {
        paddingLeft: '28px',
        width: '18.2%',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        '& p': {
            fontSize: '13px',
        }
    },
    'projectinfo-url-content': {
        position: 'relative',
        paddingLeft: '28px',
        paddingRight: '62px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        flex: 1
    },
    'projectinfo-url-text': {
        fontSize: '13px',
        lineHeight: '16px',
        '&.feature': {
            marginRight: '60px',
        }
    },
    'projectinfo-name': {
        padding: '10px 0 10px 28px',
        width: '33%',
        fontSize: '13px',
        borderRight: '0.5px solid #CCCCCC',
        position: 'relative',
        '&.feature': {
            width: '20%',
        }
    },
    'feature-area': {
        padding: '10px 0 10px 28px',
        width: '13%',
        fontSize: '13px',
        borderRight: '0.5px solid #CCCCCC'
    },
    'projectinfo-area': {
        padding: '10px 0 10px 28px',
        width: '12.8%',
        fontSize: '13px',
        borderRight: '0.5px solid #CCCCCC'
    },
    'aws-account': {
        padding: '10px 0 10px 28px',
        width: '18.2%',
        fontSize: '13px',
        borderRight: '0.5px solid #CCCCCC'
    },
    'projectinfo-url': {
        padding: '10px 0 10px 28px',
        fontSize: '13px',
        position: 'relative',
        flex: 1
    },
    'area-loading': {
        color: '#E66400',
        width: '16px !important',
        height: '16px !important',
    },
    copyIcon: {
        width: '24px',
        height: '24px',
    },
    btnsArea: {
        textAlign: 'center',
        margin: '0 auto'
    },
    createBranchContainer: {
        '& .MuiDialog-paper': {
            width: '550px',
            maxWidth: '550px',
        }
    },
    branchListContainer: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '500px',
        }
    }
}));

const CreateNewBranchDialog = ({projectName, pipelineOwner, open, handleClose}) => {

    const { isCreating, createBranch } = useCreateFeatureBranch(projectName, pipelineOwner);

    const dispatch = useDispatchReducerContext();
    const handleAwsAuthErr = useHandleAwsAuthErr();

    const classes = useStyles();

    const [branchNameValidateFail, setBranchNameValidateFail] = useState(false);
    const [commitIdValidateFail, setCommitIdValidateFail] = useState(false);
    const [featureName, setFeatureName] = useState('');
    const [commitId, setCommitId] = useState('');
    const [featureNameErrortext, setFeatureNameErrortext] = useState('');
    const [CommitIdErrortext, setCommitIdErrortext] = useState('');

    useEffect(() => {
        if (!open) {
            setFeatureName('');
            setCommitId('');
            setBranchNameValidateFail(false);
            setCommitIdValidateFail(false);
            setFeatureNameErrortext('');
            setCommitIdErrortext('');
        }
    }, [open]);

    const featureNameChangeHandle = (e) => {
        setFeatureName(e.target.value);
    }

    const commitIdChangeHandle = (e) => {
        setCommitId(e.target.value);
    }

    const handleCreateBranch = () => {
        if (!featureName || featureName === '') {
            setFeatureNameErrortext('Please enter feature branch name');
            setBranchNameValidateFail(true);
            return;
        }
        let pattern = /^[A-Za-z][A-Za-z0-9-]*$/;
        let result = pattern.test(featureName);
        if (!result) {
            setBranchNameValidateFail(true);
            setFeatureNameErrortext('Please enter letters, numbers, or hyphens, and the first character must be a letter');
            return;
        }

        if (!commitId || commitId === '') {
            setCommitIdErrortext('Please enter commit id');
            setCommitIdValidateFail(true);
            return;
        }

        setBranchNameValidateFail(false);
        setFeatureNameErrortext('');

        createBranch(featureName, commitId).then(() => {
            dispatch({
                type: SHOW_TOAST,
                payload: {
                    message: "Create feature branch triggered successfully, it will take 10-15 mins to complete",
                    severity: 'success'
                }
            });
            handleClose();
        }).catch((err) => {
            if (!handleAwsAuthErr(err)) {
                console.error(err);
                dispatch({
                    type: SHOW_TOAST,
                    payload: {
                        message: err.message,
                        severity: 'error'
                    }
                });
            }
        })
    }

    return <CommonDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        disableEscapeKeyDown
        className={classes.createBranchContainer}
    >
        <CommonDialogTitle >Create Feature Branch</CommonDialogTitle>
        <CommonDialogContent>
            <CommonTextField
                required
                InputLabelProps={{ shrink: true }}
                margin="normal"
                error={branchNameValidateFail}
                value={featureName}
                onChange={(e) => featureNameChangeHandle(e)}
                label="Branch Name"
                placeholder="The name of the new branch to create."
                helperText={featureNameErrortext}
                fullWidth
            />
            <CommonTextField
                required
                InputLabelProps={{ shrink: true }}
                margin="normal"
                error={commitIdValidateFail}
                value={commitId}
                onChange={(e) => commitIdChangeHandle(e)}
                label="Commit Id"
                placeholder="The ID of the commit to point the new branch to."
                helperText={CommitIdErrortext}
                fullWidth
            />
        </CommonDialogContent>
        <CommonDialogActions className={classes.btnsArea}>
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
            <CommonButton onClick={handleCreateBranch} color="primary" loading={isCreating} autoFocus startIcon={<Add />}>
                Create
            </CommonButton>
        </CommonDialogActions>
    </CommonDialog>
}

const BranchListDialog = ({projectName, branchs, open, handleClose}) => {
    const classes = useStyles();
    const history = useHistory();

    const handleOnClick = (branch) => {
        handleClose();
        history.push(`${branch.featurename.S}-${projectName}`);
    }

    return <CommonDialog
        open={open}
        onClose={handleClose}
        className={classes.branchListContainer}
        aria-labelledby="alert-dialog-title"
        disableEscapeKeyDown
    >
        <CommonDialogTitle >Existing Feature Branchs</CommonDialogTitle>
        <CommonDialogContent>
            <List component="nav" aria-label="secondary mailbox folders">
                {branchs.map(item => <ListItem button key={item.featurename.S} onClick={() => handleOnClick(item)}>
                    <ListItemText primary={
                        <>
                            <img src={gitIcon} alt='branch list' className={classes.gitIcon} style={{position:'relative', top: '8px', left: '-8px'}} />
                            {item.featurename.S}-{projectName}
                        </>
                    } />
                </ListItem>)}
            </List>
        </CommonDialogContent>
        <CommonDialogActions className={classes.btnsArea}>
            <CancelButton onClick={handleClose}>Close</CancelButton>
        </CommonDialogActions>
    </CommonDialog>
}

const MetaInfo = (props) => {
    const classes = useStyles();

    const params = useParams();
    const pipelineName = params.id;
    
    const dispatch = useDispatchReducerContext();
    const storeState = useStateReducerContext();
    const [copyTooltip, setCopyTooltip] = useState('Copy');

    const projectName = props.projectName;
    const pipetype = props.pipetype;
    const gitLink = `git clone codecommit::eu-west-1://${projectName}`;

    const featureBranchInfo = useFetchFeatureBranchs(!props.feature, projectName, props.owner);

    const isBookmarked = useMemo(() => {
        return storeState.bookmarks.includes(pipelineName);
    }, [pipelineName, storeState.bookmarks]);
    

    const handleBookmarkClick = () => {
        dispatch({
            type: isBookmarked ? REMOVE_BOOKMARK : ADD_BOOKMARK,
            payload: {
                bookmark: pipelineName
            }
        });
    }

    const handleCopy = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(gitLink);
            setCopyTooltip('Copied');
        }
    }

    const [isShowCreateNewBranch, setIsShowCreateNewBranch] = useState(false);
    
    const handleCreateBranchClick = () => {
        const MAX_BRANCH = 3;
        if (featureBranchInfo.branchs.length >= MAX_BRANCH) {
            dispatch({
                type: SHOW_TOAST,
                payload: {
                    message: `The maximum number of branches has been reached: ${MAX_BRANCH}`,
                    severity: 'error'
                }
            });
        } else {
            setIsShowCreateNewBranch(true);
        }
    }

    const [isShowBranchs, setIsShowBranchs] = useState(false);
    const handleShowBranchsClick = () => {
        setIsShowBranchs(true);
    }

    const displayProjectName = props.projectName && props.projectName !== '' ? props.projectName : '-';

    return <>
        <Box className={classes.projectinfotitle}>
            <Typography className={[classes['projectinfo-name'], 'feature'].join(' ')}>
                Pipeline/Repository Name
            </Typography>
            {
                props.feature && <Typography className={classes['feature-area']}>
                    Feature Name
                </Typography>
            }
            <Typography className={classes['projectinfo-area']}>
                Product Area
            </Typography>
            <Typography className={classes['aws-account']}>
                AWS Account
            </Typography>
            <Typography className={classes['projectinfo-url']}>
                Clone URL
            </Typography>
        </Box>
        <Box className={classes.projectinfocontent}>
            <Box className={[classes['projectinfo-name-content'], 'feature'].join(' ')}>
                {
                    props.isLoading ? 
                        <CircularProgress className={classes['area-loading']} /> : 
                        <Typography className='pipelinename'>
                        {
                            props.feature ? <Link to={`/project/${props.projectName}`}>
                                {displayProjectName}
                            </Link> : <>{pipelineName}</>
                        }
                        </Typography>
                }
                <CommonTooltip title={isBookmarked ? 'Bookmarked' : 'Bookmark'}>
                    <IconButton onClick={handleBookmarkClick} className={[classes['icon-btn'], 'bookmark'].join(' ')}>
                        <img src={isBookmarked ? bookmarkedIcon : unbookmarkedIcon} alt='bookmark' className={classes.bookmarkIcon} />
                    </IconButton>
                </CommonTooltip>
            </Box>
            {
                props.feature && <Box className={classes['feature-area-content']}>
                    {
                        props.isLoading ? <CircularProgress className={classes['area-loading']} /> : <Typography>{props.feature && props.feature !== '' ? props.feature : '-'}</Typography>
                    }
                </Box>
            }
            <Box className={classes['projectinfo-area-content']}>
                {
                    props.isLoading ? <CircularProgress className={classes['area-loading']} /> : <Typography>{props.owner && props.owner !== '' ? props.owner : '-'}</Typography>
                }
            </Box>
            <Box className={classes['aws-account-content']}>
                {
                    props.isLoading ? <CircularProgress className={classes['area-loading']} /> : <Typography>{props.isDataLakeAccount ? 'Data lake account' : 'Main account'}</Typography>
                }
            </Box>
            <Box className={classes['projectinfo-url-content']}>
                <Typography className={`${classes['projectinfo-url-text']}${!props.feature ? ' feature' : ''}`}>
                    {gitLink}
                </Typography>

                <div className={classes.gitLinkBtnArea}>
                    <CommonTooltip title={copyTooltip}>
                        <IconButton onClick={handleCopy} onMouseEnter={() => setCopyTooltip('Copy')} className={classes['icon-btn']}>
                            <img src={copyIcon} alt='copy' className={classes.copyIcon} />
                        </IconButton>
                    </CommonTooltip>
                    {
                        featureBranchInfo && ['application', 'lambda'].includes(pipetype) && <>
                            {
                                featureBranchInfo.branchs.length > 0 && <CommonTooltip title="Feature Branchs List">
                                    <IconButton onClick={handleShowBranchsClick} className={`${classes['git-icon']}`}>
                                        <img src={gitIcon} alt='branch list' className={classes.gitIcon} />
                                        <img src={listIcon} alt='branch list' className={classes.smallGitIcon} />
                                    </IconButton>
                                </CommonTooltip>
                            }
                            <CommonTooltip title="Create Feature Branch">
                                <IconButton onClick={handleCreateBranchClick} className={`${classes['git-icon']}`}>
                                    <img src={gitIcon} alt='branch list' className={classes.gitIcon} />
                                    <img src={addIcon} alt='branch list' className={classes.smallGitIcon} />
                                </IconButton>
                            </CommonTooltip>
                        </>
                    }
                </div>
            </Box>
        </Box>

        <CreateNewBranchDialog projectName={projectName} pipelineOwner={props.owner} open={isShowCreateNewBranch} handleClose={() => setIsShowCreateNewBranch(false)} />
        <BranchListDialog projectName={projectName} branchs={featureBranchInfo?.branchs ?? []} open={isShowBranchs} handleClose={() => setIsShowBranchs(false)} />
    </>
}

export default MetaInfo;